import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import classNames from 'classnames'
import ADND from '@aytm/lib/adnd/ADND'
import mcss from './SectionToggle.module.scss'
import type { SectionNameValidator } from './SectionToggle.hooks'
import { increaseLegibilityForLabel } from '@aytm/app/insights/dashboard/components/ui/elements/CustomDropdown.logic'
import type { TargetType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
/**
 * itemOptionTemplate
 */

export type SectionDropdownOptionItem = {
  code: string
  name: string
  isGroup: boolean
  itemsInGroup?: number
  items?: Array<string>
}

export const itemOptionTemplate = (option: SectionDropdownOptionItem) => {
  if (!option) {
    return null
  }
  if (option.isGroup) {
    return (
      <div title={option.itemsInGroup ? option.items.join(', ') : 'empty'}>
        <strong>{option.name}</strong> -{' '}
        <span>{option.itemsInGroup ? `${option.itemsInGroup} attributes` : 'empty'}</span>
      </div>
    )
  }
  return <>{increaseLegibilityForLabel(option.name, 12)}</>
}

/**
 * EditSectionNameForm
 */

export type EditSectionNameProps = {
  initialValue?: string
  target: TargetType
  onNameReady(name: string): void
  onNameValidate?: SectionNameValidator
  onDismiss?(): void
}

export const EditSectionNameForm: React.FC<EditSectionNameProps> = ({
  target,
  onNameReady,
  onDismiss,
  onNameValidate,
  initialValue = ''
}) => {
  const [sectionName, setSectionName] = useState(initialValue || '')
  const [isSectionNameInvalid, setIsSectionNameInvalid] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const isEditing = Boolean(initialValue)

  const isDisabled =
    isSectionNameInvalid || (isEditing ? sectionName.trim() === initialValue.trim() : !sectionName.trim())

  const handleEditSectionNameDismiss = useCallback(() => {
    setSectionName(initialValue)
    setIsSectionNameInvalid(false)
    onDismiss?.()
  }, [initialValue, onDismiss])

  const handleEditSectionNameCommit = useCallback(
    (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (onNameValidate) {
        const [isValid, errorMessage] = onNameValidate(sectionName, target)
        if (isValid) {
          setValidationError(null)
        } else {
          setIsSectionNameInvalid(true)
          setValidationError(errorMessage)
          return
        }
      }
      const candidateSectionName = sectionName.trim()
      setSectionName(candidateSectionName)
      if (candidateSectionName === initialValue) {
        return
      }
      setIsSectionNameInvalid(false)
      onNameReady(candidateSectionName)
      onDismiss?.()
    },
    [onNameValidate, sectionName, target, initialValue, onNameReady, onDismiss]
  )

  useEffect(() => {
    setSectionName(initialValue)
    setIsSectionNameInvalid(false)
    setValidationError(null)
  }, [initialValue])

  return (
    <form action="" onSubmit={handleEditSectionNameCommit}>
      <div className={mcss['section-name-field']}>
        <label className={mcss['section-name-label']}>
          {initialValue ? 'Edit section name:' : 'Name new section:'}
        </label>
        <InputText
          type="text"
          name="section"
          className={classNames(mcss['section-name-input'], {
            ['p-invalid']: isSectionNameInvalid
          })}
          required
          autoComplete="off"
          aria-autocomplete="none"
          value={sectionName}
          placeholder="Enter section name"
          onChange={event => {
            setValidationError(null)
            setIsSectionNameInvalid(false)
            setSectionName(event.target.value)
          }}
        />
        {Boolean(validationError) && (
          <small className={classNames(mcss['section-name-error'], 'p-error')}>{validationError}</small>
        )}
      </div>
      <div className={mcss['section-name-buttons']}>
        <Button
          type="button"
          className="p-button-link ip-button-secondary"
          label="Cancel"
          onClick={handleEditSectionNameDismiss}
        />
        <Button
          type="submit"
          className="p-button-link"
          label="Save"
          disabled={isDisabled}
          onClick={handleEditSectionNameCommit}
        />
      </div>
    </form>
  )
}

export type MiniDialogProps = {
  message: string | React.ReactNode
  messageClassName?: string
  optionNames?: string[]
  optionClassNames?: string[]
  onConfirm?(): void
  onDismiss?(): void
}

const MiniDialogImpl: React.FC<MiniDialogProps> = ({
  message,
  messageClassName,
  onConfirm,
  onDismiss,
  optionNames,
  optionClassNames
}) => (
  <div className={mcss['mini-dialog']}>
    <div className={classNames(mcss['mini-dialog-body'], messageClassName)}>{message}</div>
    <div className={mcss['mini-dialog-controls']}>
      {(optionNames ?? ['No', 'Yes']).map((optionName, index) => (
        <button
          type="button"
          role="button"
          key={optionName}
          className={
            optionClassNames?.[index] ??
            [
              classNames(mcss['mini-dialog-negative'], 'p-button p-button-link ip-button-secondary'),
              classNames(mcss['mini-dialog-positive'], 'p-button p-button-link')
            ][index]
          }
          onClick={0 === index ? onDismiss : onConfirm}
        >
          {optionName}
        </button>
      ))}
    </div>
  </div>
)

export const MiniDialog = React.memo(
  MiniDialogImpl,
  (prev, next) =>
    prev.optionNames?.join() === next.optionNames?.join() &&
    prev.optionClassNames?.join() === next.optionClassNames?.join() &&
    prev.message === next.message &&
    prev.messageClassName === next.messageClassName
)

export const DragHandle: React.FC<
  React.PropsWithChildren<{
    className?: string
    dragLayerClassName?: string
    dragType: string // e.g.: 'attribute'
    dragData: unknown
    parent?: number
    child?: number
    onDrag?(data: { action: 'start' | 'end'; dragType: string; dragData: unknown }): void
  }>
> = ({ className, dragType, dragData, dragLayerClassName, children, onDrag, parent = 1, child = 0 }) => {
  const onDragRef = useRef<{ (data: { action: 'start' | 'end'; dragType: string; dragData: unknown }): void } | null>(
    null
  )

  if (!onDragRef.current) {
    onDragRef.current = onDrag
  }

  const dragSource = useMemo<{ connect(node: HTMLElement): void; disconnect(): void }>(
    () =>
      ADND.sources
        .draggableData({
          type: dragType,
          parent,
          child,
          lockX: true,
          dragLayerClassName: classNames('PRAYTMTheme', mcss['drag-handle-ghost'], dragLayerClassName)
        })
        .fetchData(() => dragData)
        .onStart(() => {
          if (onDragRef.current) {
            onDragRef.current({ action: 'start', dragData, dragType })
          }
        })
        .onEnd(() => {
          if (onDragRef.current) {
            onDragRef.current({ action: 'end', dragData, dragType })
          }
        }),
    [child, dragData, dragLayerClassName, dragType, parent]
  )

  useEffect(
    () => () => {
      dragSource.disconnect()
    },
    [dragSource]
  )

  return (
    <div
      className={classNames('PRAYTMTheme', mcss['drag-handle'], className)}
      ref={node => void dragSource.connect(node)}
      onClick={event => event.stopPropagation()}
    >
      {children ? children : <i className={classNames(mcss['icon'], `icon-aytmfd-hotdog`)} />}
    </div>
  )
}
